'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { ExternalLink, Play, Tv, X } from 'lucide-react';

const TWITCH_CHANNEL = 'Harveyridesbikes';
const TWITCH_PARENT_DOMAINS = ['gentlemansride.com', 'www.gentlemansride.com'];
const TWITCH_CHANNEL_URL = `https://www.twitch.tv/${TWITCH_CHANNEL}`;
const HARVEY_INSTAGRAM_URL = 'https://www.instagram.com/harveyridesbikes';

// DGR London goes live ~09:30 BST on ride day. May 17 2026 is in BST
// (UTC+1), so 09:30 BST == 08:30 UTC. Used to render a local-time
// conversion under the section subtitle for visitors elsewhere in the world.
const GO_LIVE_UTC_MS = Date.UTC(2026, 4, 17, 8, 30);

// Twitch's player iframe requires the embedding page's hostname to be in
// the parent allowlist or it'll respond with X-Frame-Options: DENY. We pass
// the production hosts plus the current hostname so the embed also works on
// staging / preview / local-dev URLs. We use player.twitch.tv directly
// instead of the JS Embed API — the JS API wraps the player in
// embed.twitch.tv which has stricter framing rules and was getting refused.
function buildPlayerSrc(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const parents = Array.from(new Set([...TWITCH_PARENT_DOMAINS, hostname].filter(Boolean)));
  const params = new URLSearchParams();
  params.set('channel', TWITCH_CHANNEL);
  params.set('autoplay', 'true');
  params.set('muted', 'false');
  for (const p of parents) params.append('parent', p);
  return `https://player.twitch.tv/?${params.toString()}`;
}

export function TwitchLiveCard() {
  const [playing, setPlaying] = useState(false);

  // Render the "Going live" local-time conversion client-side only so SSR
  // doesn't bake in the server's timezone. SSR ships nothing (null), then
  // hydration fills in the bracketed local time on first paint.
  const [localGoLive, setLocalGoLive] = useState<string | null>(null);
  useEffect(() => {
    try {
      setLocalGoLive(
        new Intl.DateTimeFormat(undefined, {
          hour: 'numeric',
          minute: 'numeric',
          timeZoneName: 'short',
        }).format(new Date(GO_LIVE_UTC_MS)),
      );
    } catch {
      // Browser lacks Intl support — silently omit the bracket.
    }
  }, []);

  return (
    <div className="mt-16 md:mt-20">
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-dgr-gold-hover">
          Live from London
        </p>
        <h2 className="mt-3 text-2xl font-bold text-stone-950 md:text-3xl">
          Watch the DGR London ride on Twitch
        </h2>
        <p className="mt-3 text-sm text-stone-700 md:text-base">
          <a
            href={HARVEY_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-stone-950 underline-offset-4 transition-colors hover:text-dgr-gold-hover hover:underline"
          >
            HarveyRidesBikes
          </a>{' '}
          is streaming the ride live — tap in to ride along.
        </p>
        <p className="mt-2 text-xs text-stone-600 md:text-sm">
          Going live approximately 9.30am London Time
          {localGoLive ? <span className="text-stone-500"> ({localGoLive})</span> : null}
        </p>
      </div>

      <div className="mx-auto mt-10 max-w-5xl">
        {!playing ? (
          <ThumbnailCard onPlay={() => setPlaying(true)} />
        ) : (
          <PlayerCard onClose={() => setPlaying(false)} />
        )}
      </div>
    </div>
  );
}

function ThumbnailCard({ onPlay }: { onPlay: () => void }) {
  // Thumbnail facade — no Twitch script or iframe loads until the user opts
  // in. Rendered as role="button" so the inline <a> links to Harvey's
  // Instagram can be real anchors (anchors can't nest in <button>).
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPlay}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPlay();
        }
      }}
      className="group relative block w-full cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-stone-900 text-left shadow-2xl shadow-black/40 transition-transform duration-500 ease-out hover:-translate-y-1 hover:border-dgr-gold/40 hover:shadow-dgr-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-dgr-gold focus-visible:ring-offset-2 focus-visible:ring-offset-dgr-gold-tint"
    >
      <div className="relative h-[30rem] md:h-[32rem]">
        <Image
          src="/images/dgr/HarveyRidesBikes.png"
          alt=""
          fill
          sizes="(min-width: 1024px) 1024px, 100vw"
          className="object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/20 to-transparent" />
        <div className="absolute inset-0 bg-stone-950/20 transition-opacity duration-500 group-hover:bg-stone-950/10" />

        <div className="absolute right-5 top-5 inline-flex items-center gap-2 rounded-full border border-red-400/40 bg-red-500/25 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-50 backdrop-blur-md">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          Live on Twitch
        </div>

        <div className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur-md transition-all duration-500 group-hover:scale-110 group-hover:border-dgr-gold group-hover:bg-dgr-gold group-hover:text-stone-950">
          <Play className="h-8 w-8 fill-current" strokeWidth={0} />
        </div>

        <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-dgr-gold transition-all duration-500 group-hover:w-full" />
      </div>
    </div>
  );
}

function PlayerCard({ onClose }: { onClose: () => void }) {
  // Build the player URL once on mount so the iframe doesn't reload on
  // every render. The component only mounts client-side (parent state
  // flipped on click), so window is safe to reference inside the init.
  const [src] = useState(() => buildPlayerSrc());

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-stone-950 shadow-2xl shadow-black/40">
      <div className="relative aspect-video w-full bg-black">
        <iframe
          src={src}
          title="HarveyRidesBikes on Twitch"
          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
          allowFullScreen
          className="h-full w-full border-0"
          scrolling="no"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close player"
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-stone-950/70 text-stone-200 backdrop-blur-md transition-colors hover:border-dgr-gold/60 hover:bg-stone-950/90 hover:text-white"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-900 px-5 py-3 text-xs text-stone-400">
        <span className="inline-flex items-center gap-2">
          <Tv className="h-3.5 w-3.5 text-dgr-gold" strokeWidth={1.8} />
          <a
            href={HARVEY_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-white underline-offset-4 hover:text-dgr-gold hover:underline"
          >
            @harveyridesbikes
          </a>
          <span aria-hidden="true">·</span>
          <span>DGR London</span>
        </span>
        <a
          href={TWITCH_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-semibold text-dgr-gold transition-colors hover:text-dgr-gold-hover"
        >
          Open on Twitch
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
        </a>
      </div>
    </div>
  );
}
